import {ICategory} from "../../model/ICategory";

export const ROOT_CATEGORY_OPTION: ICategory = {
    id: -1,
    name: 'Root',
    level: 1,
    parent_id: null,
    sort_order: 0,
};

export type DropPlacement = 'before' | 'after' | 'into';

export interface CategoryMove {
    parentId: number | null;
    position: number;
}

export interface CategorySnapshot {
    id: number;
    name: string;
    parent_id: number | null;
    position: number;
}

export function getDescendantIds(categories: ICategory[], categoryId: number): Set<number> {
    const ids = new Set<number>();
    const index = categories.findIndex((category) => category.id === categoryId);
    if (index < 0) {
        return ids;
    }
    const startLevel = categories[index].level;
    for (let i = index + 1; i < categories.length; i++) {
        if (categories[i].level <= startLevel) {
            break;
        }
        ids.add(categories[i].id);
    }
    return ids;
}

export function categoryHasChildren(categories: ICategory[], categoryId: number): boolean {
    return getDescendantIds(categories, categoryId).size > 0;
}

export function getSiblings(categories: ICategory[], parentId: number | null, excludeId?: number): ICategory[] {
    return categories.filter((category) =>
        (category.parent_id ?? null) === parentId && category.id !== excludeId
    );
}

export function getSiblingIndex(categories: ICategory[], category: ICategory): number {
    return getSiblings(categories, category.parent_id ?? null).findIndex((item) => item.id === category.id);
}

export function snapshotCategory(categories: ICategory[], category: ICategory): CategorySnapshot {
    return {
        id: category.id,
        name: category.name,
        parent_id: category.parent_id ?? null,
        position: Math.max(getSiblingIndex(categories, category), 0),
    };
}

export function getDropPlacement(event: {currentTarget: EventTarget & Element, clientY: number}): DropPlacement {
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = (event.clientY - rect.top) / Math.max(rect.height, 1);
    if (ratio < 0.28) {
        return 'before';
    }
    if (ratio > 0.72) {
        return 'after';
    }
    return 'into';
}

export function resolveDrop(
    categories: ICategory[],
    draggingId: number,
    target: ICategory,
    placement: DropPlacement,
): CategoryMove | null {
    if (draggingId === target.id) {
        return null;
    }
    if (getDescendantIds(categories, draggingId).has(target.id)) {
        return null;
    }

    const dragged = categories.find((category) => category.id === draggingId);
    if (!dragged) {
        return null;
    }

    let parentId: number | null;
    let position: number;
    if (placement === 'into') {
        parentId = target.id;
        position = getSiblings(categories, parentId, draggingId).length;
    } else {
        parentId = target.parent_id ?? null;
        const siblings = getSiblings(categories, parentId, draggingId);
        const targetIndex = siblings.findIndex((category) => category.id === target.id);
        if (targetIndex < 0) {
            return null;
        }
        position = placement === 'before' ? targetIndex : targetIndex + 1;
    }

    if (parentId === draggingId || (parentId != null && getDescendantIds(categories, draggingId).has(parentId))) {
        return null;
    }

    if ((dragged.parent_id ?? null) === parentId) {
        const currentIndex = getSiblingIndex(categories, dragged);
        if (position === currentIndex) {
            return null;
        }
    }

    return {parentId, position};
}

export function getApiErrorMessage(error: unknown, fallback = 'Request failed'): string {
    if (!error || typeof error !== 'object' || !('data' in error)) {
        return fallback;
    }
    const data = (error as {data?: unknown}).data;
    if (typeof data === 'string') {
        return data;
    }
    if (!data || typeof data !== 'object') {
        return fallback;
    }
    const messages: string[] = [];
    for (const value of Object.values(data as Record<string, unknown>)) {
        if (typeof value === 'string') {
            messages.push(value);
        } else if (Array.isArray(value)) {
            messages.push(...value.filter((item): item is string => typeof item === 'string'));
        }
    }
    return messages.join(' ') || fallback;
}
